import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Chip,
  Stack,
} from "@mui/material";
import { db } from "../../utils/firebase";

const ReferralsPage = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [todayReferrals, setTodayReferrals] = useState(0);

  useEffect(() => {
    const fetchReferrals = async () => {
      try {
        setLoading(true);
        const referralsRef = db.collection("referrals");

        // Query the referrals collection, ordered by createdAt in descending order (newest first)
        const snapshot = await referralsRef.orderBy("createdAt", "desc").get();

        // Get today's date at midnight for comparison
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let todayCount = 0;

        const referralsData = snapshot.docs.map((doc) => {
          const data = doc.data();

          // Create a processed data object with renamed fields
          const processedData = { ...data, id: doc.id };

          // Determine if the recipient has signed up
          const hasSignedUp = !!data.recipientSignedUpEmail;
          processedData.hasSignedUp = hasSignedUp;

          // Create combined email field based on availability
          processedData.recipientEmail =
            data.recipientSignedUpEmail || data.recipientEmail || "-";

          // Rename the name field - check all possible variations
          if (data.recipientSignedupName) {
            processedData.recipientName = data.recipientSignedupName;
            delete processedData.recipientSignedupName;
          } else if (data.recipientSignedUpName) {
            processedData.recipientName = data.recipientSignedUpName;
            delete processedData.recipientSignedUpName;
          } else {
            processedData.recipientName = "-";
          }

          // Parse createdAt for counting today's referrals
          if (data.createdAt) {
            const createdDate = data.createdAt.toDate();
            if (createdDate >= today) {
              todayCount++;
            }

            // Format createdAt timestamp for display
            processedData.createdAt = createdDate.toLocaleString();
          } else {
            processedData.createdAt = "Unknown";
          }

          // Remove fields we don't want
          delete processedData.recipientSignedUpEmail;
          delete processedData.recipientId;
          delete processedData.referrerId;

          // Double check to ensure no name field variants remain
          if (processedData.recipientSignedupName)
            delete processedData.recipientSignedupName;
          if (processedData.recipientSignedUpName)
            delete processedData.recipientSignedUpName;

          return processedData;
        });

        setTodayReferrals(todayCount);
        setReferrals(referralsData);
      } catch (err) {
        console.error("Error fetching referrals:", err);
        setError("Failed to load referrals data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchReferrals();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Determine table columns dynamically based on the first referral object
  const getTableHeaders = () => {
    if (referrals.length === 0) return [];

    // Get all keys from the first referral object
    const firstReferral = referrals[0];
    const allKeys = Object.keys(firstReferral).filter(
      (key) => key !== "id" && key !== "hasSignedUp"
    );

    // Define our desired column order
    const desiredOrder = [
      "recipientName",
      "recipientEmail",
      "referrerName",
      "referrerEmail",
    ];

    // Start with the desired order columns that exist in our data
    const finalOrder = desiredOrder.filter((key) => allKeys.includes(key));

    // Add remaining columns (excluding those already included and those we want at the end)
    const columnsToAppendAtEnd = ["method", "status", "createdAt"];
    const remainingColumns = allKeys.filter(
      (key) => !finalOrder.includes(key) && !columnsToAppendAtEnd.includes(key)
    );

    // Add remaining columns
    finalOrder.push(...remainingColumns);

    // Add method, status and createdAt at the end (if they exist)
    columnsToAppendAtEnd.forEach((column) => {
      if (allKeys.includes(column)) {
        finalOrder.push(column);
      }
    });

    return finalOrder;
  };

  const headers = getTableHeaders();

  return (
    <div>
      <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
        Referrals
      </Typography>

      <Stack direction="row" spacing={3} sx={{ mb: 4 }} alignItems="center">
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body1" sx={{ mr: 1, fontWeight: "bold" }}>
            Total Referrals:
          </Typography>
          <Chip
            label={referrals.length}
            color="primary"
            sx={{ fontWeight: "bold", fontSize: "0.9rem" }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body1" sx={{ mr: 1, fontWeight: "bold" }}>
            Today's Referrals:
          </Typography>
          <Chip
            label={todayReferrals}
            color="secondary"
            sx={{ fontWeight: "bold", fontSize: "0.9rem" }}
          />
        </Box>
      </Stack>

      {referrals.length === 0 ? (
        <Typography variant="body1">No referrals found</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader aria-label="referrals table">
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell
                    key={header}
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    {header.charAt(0).toUpperCase() + header.slice(1)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {referrals.map((referral) => (
                <TableRow
                  key={referral.id}
                  sx={{
                    backgroundColor: referral.hasSignedUp
                      ? "#e8f5e9"
                      : "inherit",
                    "&:hover": {
                      backgroundColor: referral.hasSignedUp
                        ? "#c8e6c9"
                        : "#f5f5f5",
                    },
                  }}
                >
                  {headers.map((header) => (
                    <TableCell key={`${referral.id}-${header}`}>
                      {referral[header] !== null &&
                      referral[header] !== undefined
                        ? String(referral[header])
                        : "-"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default ReferralsPage;
